import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  createAuthenticatedAxios,
  getRmApiBaseUrl,
} from './authAxiosFactory';

const api = createAuthenticatedAxios(getRmApiBaseUrl());

type RequestMethod = 'get' | 'post' | 'put' | 'delete';

/** URLs that handle patient/PHI data - do not log request/response data (HIPAA) */
const isPhiEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return (
    url.includes('conversation_details') ||
    url.includes('get/patients')
  );
};

const handleRequest = async <T>(
  method: RequestMethod,
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await api[method]<T>(
      url,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const url = error.config?.url;
      const isPhi = isPhiEndpoint(url);

      if (!isPhi) {
        console.error('Axios error response:', error.response?.data);
      }

      throw error;
    }

    const url = (error as { config?: { url?: string } })?.config?.url;
    if (!isPhiEndpoint(url)) {
      console.error('Unexpected error:', error);
    }

    throw new Error(
      `Unexpected error during ${method.toUpperCase()} request`
    );
  }
};

export const get = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return handleRequest<T>('get', url, undefined, config);
};

export const post = async <T>(
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> => {
  return handleRequest<T>('post', url, data, config);
};

export const put = async <T>(
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> => {
  return handleRequest<T>('put', url, data, config);
};

export const remove = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return handleRequest<T>('delete', url, undefined, config);
};

export default api;
